import React, { useState, useEffect, useRef } from 'react';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess, UserHasAccess } from 'components/user-access';
import PropTypes from 'prop-types';
import { ListingTable } from 'components/listing';
import { FleetService, LinkService } from 'services';
import { Tooltip } from 'components/tooltip';
import { Button } from 'components/buttons';
import moment from 'moment';
import { EventScheduleModal } from './EventScheduleModal';
import { NotificationToaster } from "components/notifications";
import { useDispatch, useSelector } from 'react-redux';
import { SetFiltering } from 'state/actions';
import { ShowHide } from 'components/layout';
import { FormTextInput, FormLocationSelector } from 'components/form-fields';
import { Filter, FilterButton } from 'components/filtering';
import { useDebounce } from 'hooks/useDebounce';
import classNames from 'classnames';
import { AssetStatus } from 'components/status/AssetStatus';
import { InternalLink } from 'components/navigation';
import { Intent, Position } from '@blueprintjs/core';
import { StringToEllipsesString } from 'components/formatting/StringToEllipsesString';
import { AssetHelperFunctions } from 'helpers';
import { AssetTypeFilterRenderer } from "pages/vehicle/asset-type-filter-renderer/AssetTypeFilterRenderer";

export function EventSchedules({
    asset,
    eventTypesChanged,
    pageState,
    eventTypes,
    loading = false,
    locationList,
    mappedAssetOptions,
    addScheduleDefaultDate
}) {
    const requiredAction = "FMS:View";
    const editAction = "FMS:Edit";
    const dispatch = useDispatch();
    const debounceTimeoutMs = 750;
    const defaultTimeoutMs = 500;
    const defaultWeekStart = -2;
    const truncateLength = 30;
    const dateFormat = "YYYY-MM-DD";
    const hubFilterCount = 5;
    const filterColumnCount = pageState === "HUB" ? hubFilterCount : 2;

    const tableHeadersProfileInital = [
        "Event Type",
        "Status",
        "Schedule",
        "Last Event",
        "Next Event"
    ];

    const tableHeadersHub = [
        "ID/Registration",
        "",
        "Fleet Number",
        "Location"
    ];

    const sortableHeaders = [
        { headerName: "ID/Registration", sortName: "registration" },
        { headerName: "Fleet Number", sortName: "fleetnumber" },
        { headerName: "Event Name", sortName: "eventtypename" },
        { headerName: "Location", sortName: "locationname" },
        { headerName: "Status", sortName: "status" }
    ];

    const actionHeader = ["Actions"];
    const canEditPage = UserHasAccess(editAction);

    const tableHeadersProfile = canEditPage ? [...tableHeadersProfileInital, ...actionHeader] : tableHeadersProfileInital;
    const [showEventScheduleModal, setShowEventScheduleModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedEventType, setSelectedEventType] = useState(null);
    const eventHubReduxName = "eventHubRedux" + pageState; //This is the shared redux for the hub
    const eventSchedulesFilterReduxName = "eventSchedulesFilterRedux" + pageState; //This is the redux for the filters
    const eventSchedulesReduxName = "eventSchedulesRedux" + pageState; //This is the page redux
    const defaultRecordSize = 25;
    const defaultSortName = pageState === "HUB" ? "ID/Registration" : "Event Name";
    const [loadingData, setLoadingData] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [tableSortBy, setTableSortBy] = useState(sortableHeaders.find(sh => sh.headerName === defaultSortName));
    const [tableSortDir, setTableSortDir] = useState("A");
    const [reduxLoaded, setReduxLoaded] = useState(false);
    const [reduxBaseLoaded, setReduxBaseLoaded] = useState(false);
    const [reduxPageLoaded, setReduxPageLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState(null);
    const [focusToSearchTerm, setFocusToSearchTerm] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [locationDefaultsToTrue, setLocationDefaultsToTrue] = useState(false);
    const [pageFilters, setPageFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedAssetId, setSelectedAssetId] = useState(asset?.assetId ?? "");
    const [startDate, setStartDate] = useState(moment().add(defaultWeekStart, 'w').format(dateFormat));
    const [endDate, setEndDate] = useState(moment().add(2, 'w').format(dateFormat));
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [expandedAssetTypes, setExpandedAssetTypes] = useState({});

    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeoutMs);
    const inputSearchRef = useRef(null);

    const reduxBaseListing = useSelector(state => state.filters[eventHubReduxName]);
    const eventSchedulesRedux = useSelector(state => state.filters[eventSchedulesReduxName]);
    const eventScheduleFilterRedux = useSelector(state => state.filters[eventSchedulesFilterReduxName]) || [];

    if (eventScheduleFilterRedux.length === 0) {
        dispatch(SetFiltering(eventSchedulesFilterReduxName, AssetHelperFunctions.defaultAssetStatusFilters));
    }

    const currentPageSize = useRef();
    const currentPageNumber = useRef();
    const currentSortBy = useRef();
    const currentSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentSearchTerm = useRef();
    const currentSelectedLocations = useRef();

    useEffect(() => {
        if (reduxBaseLoaded && reduxPageLoaded) {
            setReduxLoaded(true);
        }
    }, [reduxBaseLoaded, reduxPageLoaded]);

    useEffect(() => {
        if (!reduxPageLoaded) {
            setReduxPageLoaded(true);
            if (eventSchedulesRedux) {
                setPageNumber(eventSchedulesRedux.pageNumber);
                setPageSize(eventSchedulesRedux.pageSize);
                setTableSortDir(eventSchedulesRedux.tableSortDir);
                setTableSortBy(eventSchedulesRedux.tableSortBy);
                setSearchTerm(eventSchedulesRedux.searchTerm);
            }
            else {
                setSearchTerm("");
            }
        }
    }, [eventSchedulesRedux]);

    useEffect(() => {
        if (pageState === 'HUB' && !reduxBaseLoaded) {
            if (reduxBaseListing) {
                setStartDate(reduxBaseListing.startDate);
                setEndDate(reduxBaseListing.endDate); 
                setSelectedLocations(reduxBaseListing.locations);
            } else {
                setLocationDefaultsToTrue(true);
            }
        }
        !reduxBaseLoaded && setReduxBaseLoaded(true);
    }, [reduxBaseListing]);

    useEffect(() => {
        if (eventTypes && mappedAssetOptions) {
            //The profile page doesn't have filters
            if (pageState !== "HUB") {
                return;
            }
            let allFilters = [
                {
                    name: "ASSETSTATUSID",
                    displayName: "Asset Status",
                    items: mappedAssetOptions.assetStatusesMappings
                },
                {
                    name: "ASSETOWNERSHIPID",
                    displayName: "Asset Ownership",
                    items: mappedAssetOptions.ownershipAttributeMappings
                },
                {
                    name: "ASSETTYPE",
                    displayName: "Asset Type",
                    items: mappedAssetOptions.assetTypes
                },
                {
                    name: "EVENTTYPEID",
                    displayName: "Event Type",
                    items: eventTypes.filter(et => !et.adHoc)
                },
                {
                    name: "SCHEDULESTATUS",
                    displayName: "Schedule Status",
                    items: [
                        { id: "Scheduled", name: "Scheduled" },
                        { id: "Not Scheduled", name: "Not Scheduled" },
                        { id: "Disabled", name: "Disabled" }
                    ]
                }
            ];
            setPageFilters(allFilters);
        }
    }, [eventTypes, mappedAssetOptions]);

    useEffect(() => {
        if (!loading && reduxLoaded && debouncedSearchTerm !== null && selectedLocations) {
            fetchTableData();
        }
    }, [loading, pageSize, pageNumber, tableSortBy, tableSortDir, appliedFilters, debouncedSearchTerm, reduxLoaded, selectedLocations, asset]);


    useEffect(() => {
        if (tableRows) {
            setFocusToSearchTerm(true);
        }
    }, [tableRows]);

    useEffect(() => {
        if (focusToSearchTerm && !loading) {
            setTimeout(function () {
                if (inputSearchRef.current != null) {
                    inputSearchRef.current.focus();
                }
            }, defaultTimeoutMs);
            setFocusToSearchTerm(false);
        }
    }, [focusToSearchTerm, loading]);


    function fetchTableDataForHub(forceReload) {
        if ((!reduxLoaded ||
            //Need to do this check as we need to use the debouncedSearchTerm, but we need to only load once if the seachTerm is set in the redux so once it's set we know we have the redux all loaded
            searchTerm === null ||
            (searchTerm?.length > 0 && debouncedSearchTerm === null) ||
            !appliedFilters) ||
            !forceReload &&
            currentPageSize.current === pageSize &&
            currentPageNumber.current === pageNumber &&
            currentSortBy.current === tableSortBy &&
            currentSortDir.current === tableSortDir &&
            currentAppliedFilters.current === appliedFilters.length &&
            currentSelectedLocations.current === selectedLocations &&
            (currentSearchTerm.current ?? "") === (debouncedSearchTerm ?? "")
        ) {
            return;
        }

        if (pageNumber > 1 && (currentSearchTerm.current ?? "") !== (debouncedSearchTerm ?? "")) {
            //Can't set the page number when this changes else the debounce doesn't work. The page number change should kick this off again.
            setPageNumber(1);
            return;
        }

        setLoadingData(true);

        currentPageSize.current = pageSize;
        currentPageNumber.current = pageNumber;
        currentSortBy.current = tableSortBy;
        currentSortDir.current = tableSortDir;
        currentAppliedFilters.current = appliedFilters.length;
        currentSelectedLocations.current = selectedLocations;
        currentSearchTerm.current = (debouncedSearchTerm ?? "");

        const allFilters = [];

        if (appliedFilters && appliedFilters.length > 0) {
            allFilters.push(...appliedFilters.map(f => {
                if (f.name === "AssetTypeSubtype" && typeof f.value === "string" && f.value.startsWith("uncat-")) {
                    return { key: "AssetType", value: f.parentAssetTypeId.toString() };
                }
                return { key: f.name, value: String(f.value) };
            }));
        }

        allFilters.push(...selectedLocations.map(sl => ({ key: "Location", value: `${sl}` })));

        FleetService.searchSchedules(
            pageSize,
            pageNumber,
            debouncedSearchTerm === null ? searchTerm : debouncedSearchTerm.trim(),
            tableSortBy.sortName,
            tableSortDir,
            allFilters
        )
            .then(response => {
                mapEventAssetSchedulesToTable(response.data);
                setTotalRecords(response.totalCount);
            })
            .catch(error => {
                NotificationToaster.show(Intent.DANGER, error);
            })
            .finally(() => {
                setLoadingData(false);
                if (!initialLoadComplete) {
                    setInitialLoadComplete(true);
                }
            });
    }

    function fetchTableDataForProfile() {
        if (!asset?.assetId) {
            return;
        }

        setLoadingData(true);
        FleetService.getEventTypesForAsset(asset.assetId, false).then(function (r) {
            mapEventAssetSchedulesToTable(r);
            setTotalRecords(r.length);
        })
        .finally(() => {
            setLoadingData(false);
        });
    }

    function fetchTableData(forceReload) {
        pageState === 'HUB' ? fetchTableDataForHub(forceReload) : fetchTableDataForProfile();
    }

    function mapEventAssetSchedulesToTable(assetSchedules) {
        const rows = assetSchedules.map((assetSchedule) => {
            let scheduleSelectedAssetId = pageState === 'HUB' ? assetSchedule.assetId : asset.assetId;
            const row = [];
            let assetSchId = pageState === 'HUB' ? assetSchedule.assetStatusId : asset?.assetLocation?.assetStatus?.id;
            let assetInactive = AssetHelperFunctions.isAssetInactive(assetSchId, mappedAssetOptions.assetStatusesMappings);
            
            if (pageState === 'HUB') {
                let assetType = mappedAssetOptions.assetTypes.find(a => a.id === assetSchedule.assetTypeId)
                let assetSubtype = assetType?.subtypes ? assetType.subtypes.find(st => st.id === assetSchedule.assetTypeSubtypeId) : null;
                row.push(
                    <div className="nowrap inline-items">
                        <AssetStatus statusId={assetSchedule.assetStatusId} ownershipId={assetSchedule.assetOwnershipId} isLoading={loading} statusAttributeMappings={mappedAssetOptions.assetStatusesMappings} ownershipAttributeMappings={mappedAssetOptions.ownershipAttributeMappings} tooltipPosition={Position.RIGHT} />
                        <Tooltip position={Position.TOP} content={`Asset Profile`}>
                            <InternalLink link={LinkService.getVehicleProfileUrl(assetSchedule.assetId, 'events/event-schedules', "area=MMFH")}>{assetSchedule.registration}</InternalLink>
                        </Tooltip>
                    </div>,
                    <div className="nowrap inline-items">
                        {
                            AssetHelperFunctions.getVehicleIcon(
                                assetType?.id,
                                assetType?.name,
                                assetSubtype?.name)
                        }
                    </div>,
                    assetSchedule.fleetNumber,
                    assetSchedule.locationName
                );
            }

            row.push(
                <span className="nowrap"><i className={classNames("custom-icon-" + assetSchedule.iconName, "custom-icon keyblock-icon")} />
                    <StringToEllipsesString length={truncateLength} showTooltip={true}>{assetSchedule.eventTypeName}</StringToEllipsesString>
                </span>,
                assetSchedule.status,
                assetSchedule.assetSchedule === null ? assetSchedule.defaultSchedule : `${assetSchedule.assetSchedule} - Overridden`,
                assetSchedule.lastCompletedEventDate ? moment(assetSchedule.lastCompletedEventDate).format("DD/MM/YYYY") : "-",
                assetSchedule.nextEventDate ? moment(assetSchedule.nextEventDate).format("DD/MM/YYYY") : "-"
            );

            if (canEditPage) {
                if (assetInactive) {
                    row.push(<></>);
                    return row;
                }

                const actions = [];
                const isTaxOrMot =
                    assetSchedule.defaultSchedule === "Special Schedule" &&
                    (assetSchedule.eventTypeName === "Tax" || assetSchedule.eventTypeName === "MOT");

                if (!assetSchedule.disabled) {
                    if (assetSchedule.scheduled) {
                        if (!isTaxOrMot) {
                            actions.push(
                                <Tooltip key={`edit-${scheduleSelectedAssetId}-${assetSchedule.id}`} content={`Edit`}>
                                    <Button
                                        onClick={(e) => onEditSchedule(assetSchedule, scheduleSelectedAssetId, e)}
                                        icon="edit"
                                        iconOnly={true}
                                        minimal={true}
                                        large={false}
                                    />
                                </Tooltip>
                            );
                            actions.push(
                                <Tooltip key={`disable-${scheduleSelectedAssetId}-${assetSchedule.id}`} content={`Disable`}>
                                    <Button
                                        onClick={(e) => onChangeScheduleDisableState(assetSchedule, scheduleSelectedAssetId, e)}
                                        icon="ban-circle"
                                        iconOnly={true}
                                        minimal={true}
                                        large={false}
                                    />
                                </Tooltip>
                            );
                        }
                    } else {
                        actions.push(
                            <Tooltip key={`add-${scheduleSelectedAssetId}-${assetSchedule.id}`} content={`Add`}>
                                <Button
                                    onClick={(e) => onAddSchedule(assetSchedule, scheduleSelectedAssetId, e)}
                                    icon="plus"
                                    iconOnly={true}
                                    minimal={true}
                                    large={false}
                                />
                            </Tooltip>
                        );
                    }
                } else {
                    actions.push(
                        <Tooltip key={`enable-${scheduleSelectedAssetId}-${assetSchedule.id}`} content={`Enable`}>
                            <Button
                                onClick={(e) => onChangeScheduleDisableState(assetSchedule, scheduleSelectedAssetId, e)}
                                icon="reset"
                                iconOnly={true}
                                minimal={true}
                                large={false}
                            />
                        </Tooltip>
                    );
                }
                row.push(
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center"
                        }}
                    >
                        {actions}
                    </div>
                );
            }
            return row;
        });
        setTableRows(rows);
    }

    function onAddSchedule(eventType, assetIdValue, e) {
        e.stopPropagation();
        setModalMode('add');
        setSelectedEventType(eventType);
        setShowEventScheduleModal(true);
        setSelectedAssetId(assetIdValue);
    }

    function onEditSchedule(eventType, assetIdValue, e) {
        e.stopPropagation();
        setModalMode('edit');
        setSelectedEventType(eventType);
        setShowEventScheduleModal(true);
        setSelectedAssetId(assetIdValue);
    }

    function onChangeScheduleDisableState(eventType, assetIdValue, e) {
        e.stopPropagation();
        const scheduleId = eventType.scheduleId;
        const currentDisabled = eventType.disabled;

        if (currentDisabled) {
            setModalMode('edit');
            setSelectedEventType(eventType);
            setShowEventScheduleModal(true);
            setSelectedAssetId(assetIdValue);
        } else {

            setLoadingData(true);
            FleetService.setScheduleDisabled(scheduleId, true)
                .then(() => {
                    NotificationToaster.show(Intent.SUCCESS, "Schedule disabled successfully.");

                    //This will handle turning off Loading Data
                    fetchTableData(true);
                    if (eventTypesChanged) {
                        eventTypesChanged();
                    };
                })
                .catch((error) => {
                    //The error message is handled in the service so just show it.
                    NotificationToaster.show(Intent.DANGER, error);
                    setLoadingData(false);
                })
        }
    }

    function onSearchTextChange(item) {
        setSearchTerm(item);
        setEventListingDispatch(item);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
        setEventListingDispatch(null, newPageNumber, newPageSize);
    }

    function onTableSort(header, direction) {
        setTableSortDir(direction);
        setTableSortBy(header);
        setEventListingDispatch(null, null, null, header, direction);
    }

    function setEventListingDispatch(newSearchTerm, newPageNumber, newPageSize, newTableSortBy, newTableSortDir) {
        dispatch(SetFiltering(eventSchedulesReduxName, {
            searchTerm: newSearchTerm ?? searchTerm,
            pageNumber: newPageNumber ?? pageNumber,
            pageSize: newPageSize ?? pageSize,
            tableSortBy: newTableSortBy ?? tableSortBy,
            tableSortDir: newTableSortDir ?? tableSortDir
        }));
    }

    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        dispatch(SetFiltering(eventHubReduxName, {
            startDate: startDate,
            endDate: endDate,
            locations: locations
        }));
        if (initialLoadComplete) {
            setPageNumber(1);
        }
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    function onFilterChange(listingFilters) {
        if (initialLoadComplete) {
            setPageNumber(1);
        }
        setAppliedFilters(listingFilters ?? []);
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div>
                    <ShowHide
                        evaluator={pageState === 'HUB'}
                        show={(
                            <div className="filter-controls inline-items spacer-bottom-small">
                                <div className="inline-item fill">
                                    <div className="form-field-full">
                                        <FormTextInput inputRef={inputSearchRef} placeholder="Search fleet" onChange={(x) => onSearchTextChange(x.target.value)} value={searchTerm} large disabled={loading || loadingData} icon="search" id="user-search-field" />
                                    </div>
                                </div>
                                <FormLocationSelector
                                    businessArea=""
                                    loading={loading || loadingData}
                                    selectedLocations={selectedLocations}
                                    setSelectedLocations={onSelectedLocationsChange}
                                    useHierarchy={false}
                                    useCustomLocations
                                    customLocations={locationList}
                                    useLocationDefaults={locationDefaultsToTrue}
                                    locationDefaultsToTrue={locationDefaultsToTrue}
                                />
                                <Filter
                                    filterName={eventSchedulesFilterReduxName}
                                    visible={showFilters && reduxLoaded}
                                    filters={pageFilters}
                                    onUpdate={onFilterChange}
                                    loading={loading}
                                    onClickOutside={onToggleFilter}
                                    numOfColumns={filterColumnCount}
                                    customRenderers={{
                                        ASSETTYPE: (props) => (
                                            <AssetTypeFilterRenderer
                                                key="asset-Type-filter-renderer"
                                                {...props}
                                                expandedAssetTypes={expandedAssetTypes}
                                                setExpandedAssetTypes={setExpandedAssetTypes}
                                            />
                                        )
                                    }}
                                /> 
                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                            </div>
                        )}
                    />

                    <ListingTable
                        id="listing-table-asset-upcoming-events"
                        headers={pageState === 'HUB' ? [...tableHeadersHub, ...tableHeadersProfile] : tableHeadersProfile}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={loading || loadingData}
                        pageable={pageState === 'HUB'}
                        onPagingChange={onPagingChange}
                        sortable={pageState === 'HUB'}
                        sortableHeaders={sortableHeaders}
                        sortedBy={tableSortBy}
                        sortedDir={tableSortDir}
                        onSort={onTableSort}
                        pageSize={pageSize}
                        startPage={pageNumber}
                    />
                    <EventScheduleModal
                        isOpen={showEventScheduleModal}
                        mode={modalMode}
                        eventType={selectedEventType}
                        assetId={selectedAssetId}
                        onClose={() => setShowEventScheduleModal(false)}
                        onSave={() => {
                            setShowEventScheduleModal(false);
                            fetchTableData(true);
                            if (eventTypesChanged) {
                                eventTypesChanged();
                            };
                        }}
                        scheduleDefaultDate={addScheduleDefaultDate}
                    />
                </div>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}

EventSchedules.propTypes = {
    asset: PropTypes.string,
    eventTypesChanged: PropTypes.func,
    pageState: PropTypes.oneOf(['PROFILE', 'HUB']).isRequired,
    eventTypes: PropTypes.array,
    loading: PropTypes.bool,
    locationList: PropTypes.object,
    mappedAssetOptions: PropTypes.object,
    addScheduleDefaultDate: PropTypes.string
};